import React, { FunctionComponent, useMemo, useContext } from 'react';
import EDView from '../../../../BaseComponents/View/EDView';
import {
  InternalError,
  internalErrorBusinessComp,
} from '../../../../Components/InternalError/InternalError';
import {
  IMasterConfig,
  IDetailConfig,
} from '../../../../Types/MasterDetailTypes';
import {
  IClientBusinessModel,
  IClientAssociationModel,
} from '../../../../Types/ClientModelTypes';
import { IColumnDef } from '../../../../Types/ColumnDefType';
import { findCollectionStatic } from '../../../../Model/avenyStaticModelUtils';

import { MdModalRoute } from '../../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import { TVisningstyp } from '../../../Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataSet/BusinessDataSet';
import MdInline from '../../../../Components/MasterDetail/Components/MdInline/MdInline';

import { BusinessDataFormContext } from '../../../Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { useItemFunctionMenuItemsType } from '../../../../Utils/ObjektVyUtils';
import { DebugViewDetail } from '../DebugViewDetail/DebugViewDetail';
import { VisningsTypContext } from '../../../Delsystem/_Common/Components/BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';
import { IMenuProps } from '../../../Delsystem/_Common/Components/FunctionMenu/FunctionMenu/FunctionMenu';
import MasterComp from '../../../Delsystem/_Common/Components/BusinessCollectionComponents/AssociationList/AssociationListMaster';

const createMasterConfig = (
  name: string,
  associationName: string,
  collection: IClientBusinessModel,
  associatedCollection: IClientBusinessModel,
  ownId: string | null,
  config?: IAssociationListConfig
): IMasterConfig => ({
  comp: MasterComp(
    collection.Associations[associationName],
    collection,
    associatedCollection,
    ownId,
    config || {}
  ),
  caption: collection.RootType.SBLabel,
});

const createDetailConfig = (
  collection?: IClientBusinessModel,
  fullPage?: boolean,
  inline?: boolean
): IDetailConfig => ({
  comp: (collection
    ? DebugViewDetail(collection)
    : internalErrorBusinessComp(
        '547efbf9-39a1-4abc-afe9-951ebfa17428',
        'collection saknas'
      )) as IDetailConfig['comp'],
  caption: collection ? collection.RootType.SOLabel : 'collection saknas',
  fullPage: fullPage,
});

export interface IAssociationListConfig {
  customDefaultColumns?: IColumnDef[];
  cardLayout?: boolean;
  inCard?: boolean;
  detailsFullPage?: boolean;
  defaultVisningstyp?: TVisningstyp;
  noCaption?: boolean;
  useFunktionsMenyItems?: useItemFunctionMenuItemsType;
  customSOLabel?: string;
  disabled?: boolean;
}

export interface IAssociationListProps {
  /** id för objektet som associationen utgår från */
  ownId: string | null;
  /** collectionName för objektet som associationen utgår från */
  collectionName: string;
  associationName: string;
  /** Objekt för inställningar som inte är obligatoriska.
   *  customDefaultColumns, defaulta till andra kolumner än de som är default för typen.
   *  ska kompletteras med fler inställning för att välja andra list och detaljvyer
   */
  config?: IAssociationListConfig;
  associatedCollectionFieldName?: string;
  vyMenu?: (props: IMenuProps) => React.ReactElement<any>;
}

/**
Listar de associerade objekten för en "till många"-association.
*/
export const DebugAssociationList: FunctionComponent<
  React.PropsWithChildren<IAssociationListProps>
> = props => {
  const { visningsTyp } = useContext(VisningsTypContext);
  const formContext = useContext(BusinessDataFormContext);
  const config = useMemo<IAssociationListConfig>(
    () => ({
      ...props.config,
      disabled: formContext?.formState.editMode,
    }),
    [props.config, formContext?.formState.editMode]
  );

  const collection = findCollectionStatic(props.collectionName);
  const association: IClientAssociationModel | undefined =
    collection && collection.Associations[props.associationName];

  const associatedCollection =
    association && findCollectionStatic(association.AssociatedCollectionName);
  const masterConfig = useMemo(
    () =>
      collection &&
      associatedCollection &&
      createMasterConfig(
        props.collectionName,
        props.associationName,
        collection,
        associatedCollection,
        props.ownId,
        config
      ),
    [
      collection,
      associatedCollection,
      props.collectionName,
      props.associationName,
      props.ownId,
      config,
    ]
  );
  const detailConfig = useMemo(
    () =>
      createDetailConfig(
        associatedCollection,
        config?.detailsFullPage,
        visningsTyp === 'expanderbar lista'
      ),
    [associatedCollection, visningsTyp, config]
  );
  if (collection && masterConfig) {
    if (association) {
      if (associatedCollection) {
        return (
          <EDView
            style={
              props.config?.inCard
                ? { width: '100%', display: 'flex' }
                : { width: '100%', height: '100%', display: 'flex' }
            }
          >
            {visningsTyp !== 'expanderbar lista' && (
              <MdModalRoute
                detailRouteName={association.AssociationName}
                master={masterConfig}
                detail={detailConfig}
                collection={associatedCollection}
              />
            )}
            {visningsTyp === 'expanderbar lista' && (
              <MdInline master={masterConfig} detail={detailConfig} />
            )}
          </EDView>
        );
      }
    }
  }
  return (
    <InternalError
      message={`CollectionName ${props.collectionName}, eller association ${props.associationName} saknas`}
      id={'b4362ff1-6ddd-46f1-9d94-7866000a71c3'}
    />
  );
};

export interface ICreateAssociationListConfig {
  ownId?: string;
  collectionName: string;
  associationName: string;
  associatedCollectionFieldName: string;
  config: { customDefaultColumns?: IColumnDef[] };
}

export default DebugAssociationList;

export const createDebugAssociationList = (
  config: ICreateAssociationListConfig
): React.ComponentType<React.PropsWithChildren<IAssociationListProps>> => {
  const DebugAssociationListWithConfig: React.FC<
    React.PropsWithChildren<IAssociationListProps>
  > = props => (
    <DebugAssociationList
      {...props}
      ownId={config.ownId ?? props.ownId ?? null}
      collectionName={config.collectionName}
      associationName={config.associationName}
      associatedCollectionFieldName={config.associatedCollectionFieldName}
      config={config.config}
    />
  );

  return DebugAssociationListWithConfig;
};
